import {
  LanguageCode2,
  PlayerControlsApi,
  TrackKind,
  TrackType,
} from '@accedo/one-plugin-sdk';
import { proxy, snapshot, useSnapshot } from 'valtio';

import { controlsConfig } from '#/components/PlayerControls/config';
import { handleAdPlayback } from '#/components/PlayerControls/utils/adPlayback';
import { devtoolsApi } from '#/dev/devtools';
import { StorageKeys } from '#/service/constants';
import { ignoreError, limit } from '#/service/utils';

import { adsActions, adsStore } from '../ads/ads.store';
import { resetStore } from '../common';
// We are aware of this circular dependency, we intentionally disable eslint here until we fix it.
// eslint-disable-next-line import/no-cycle
import { playerStore } from '../player/player.store';
import { translationStore } from '../translation/translation.store';
import { upNextStore } from '../upNext/upNext.store';
import {
  MAX_PLAYBACK_SPEED,
  MIN_PLAYBACK_SPEED,
  NORMAL_SPEED,
} from './constants';
import {
  languageCode2ToLanguage,
  LanguageCode2Union,
  Languages,
} from './languages';
import {
  chooseDefaultAudio,
  prepareAudioName,
  prepareSubtitleName,
  sortLanguages,
} from './languageUtils';
import { Timer } from './Timer';

export interface StartTimerOptions {
  controls?: boolean;
  cursor?: boolean;
  immediately?: boolean;
}

export interface ComputeOpacityOptions {
  forceHide?: boolean;
  ignoreTouched?: boolean;
}

export enum PlayerContentState {
  Play = 'play',
  Pause = 'pause',
}

export interface ControlsStore {
  /** Seeking status */
  isSeeking: boolean;
  /** Control visibility status */
  isVisible: boolean;
  /** Spinner visibility status */
  showSpinner: boolean;
  /** Main title just above the controls visibility status */
  showVideoTitleCenter: boolean;
  /** Last touched timestamp */
  touched: number | null;
  /** Play pause icon */
  contentState: PlayerContentState.Play | PlayerContentState.Pause;
  /**
   * Player volume
   * Number between 0 and 1
   */
  volume: number;
  /**
   * Audio tracks
   * Key: [track.language, track.kind].join(',') where track is a `TrackId` type
   * Value: Human readable name of the track
   */
  audioTracks: Record<string, string>;
  /** Selected audio track which is a key of `audioTracks` */
  selectedAudioTrack: string;
  /**
   * subtitle tracks
   * Key: [track.language, track.kind].join(',') where track is a `TrackId` type
   * Value: Human readable name of the track
   */
  subtitleTracks: Record<string, string>;
  /** Selected subtitle track which is a key of `subtitleTracks` */
  selectedSubtitleTrack: string;
  /** Language selector menu opened */
  isLanguageOverlayOpen: boolean;
  /** Volume selector menu opened */
  isVolumeOverlayOpen: boolean;
  /** Playback speed selector menu opened */
  isPlaybackSpeedOverlayOpen: boolean;
  /**
   * Selected playback rate (1 = normal speed).
   * Resets to 1 on every new video since the store is reset between sessions.
   */
  playbackRate: number;
  /**
   * Full screen mode
   * Not resetting on purpose so after an up-next event the player
   * will still be in full screen mode
   */
  isFullScreen: boolean;
  showPlayer: boolean;
  /** Player controls API */
  controlsApi?: PlayerControlsApi;
}

export const controlsStore = proxy<ControlsStore>({
  isSeeking: false,
  isVisible: true,
  showSpinner: true,
  showVideoTitleCenter: true,
  touched: null,
  contentState: PlayerContentState.Play,
  volume: 0,
  audioTracks: {},
  selectedAudioTrack: 'default',
  subtitleTracks: {},
  selectedSubtitleTrack: 'off',
  isLanguageOverlayOpen: false,
  isVolumeOverlayOpen: false,
  isPlaybackSpeedOverlayOpen: false,
  playbackRate: 1,
  isFullScreen: false,
  controlsApi: undefined,
  showPlayer: false,
});

const initialControlsStore = snapshot(controlsStore) as ControlsStore;

/** Timer to hide mouse upon inactivity.
 * Calling `.tickNow()` immediately hides cursor
 * Calling `.tickAfter(delay: number)` hides cursor after `delay` ms
 * Calling `.stop()` halts all ongoing timers
 */
export const mouseStillTimer = new Timer(() => {
  if (playerStore.container && upNextStore.showControls) {
    playerStore.container.style.cursor = 'none';
  }
  controlsActions.computeOpacity({ forceHide: true });
});

/** Timer to hide player controls upon inactivity.
 * Calling `.tickNow()` immediately hides controls
 * Calling `.tickAfter(delay: number)` hides controls after `delay` ms
 * Calling `.stop()` halts all ongoing timers
 */
export const fadeControlsTimer = new Timer(() => {
  controlsActions.setVisibility(false);
});

const localStorageKeys = {
  [TrackType.audio]: StorageKeys.AUDIO_TRACK,
  [TrackType.text]: StorageKeys.SUBTITLE_TRACK,
};

export const controlsActions = {
  setPlayerControlsApi(playerControlsApi: PlayerControlsApi) {
    controlsStore.controlsApi = playerControlsApi;
  },
  loadAudio(kind: TrackKind, language: LanguageCode2Union) {
    const availableAudioTracks =
      controlsStore.controlsApi?.getAudioTracksLang?.();
    // Ignore checking the track kind if originally it was undefined
    const isAvailable = availableAudioTracks?.some(
      (track) =>
        track.language === language &&
        (track.kind === undefined || track.kind === kind)
    );

    if (isAvailable) {
      controlsActions.setLanguage(TrackType.audio, [language, kind].join(','));
      console.debug(`Restored previously used audio:`, { language, kind });
    } else if (availableAudioTracks?.length) {
      const defaultAudio = chooseDefaultAudio(
        availableAudioTracks,
        translationStore.locale
      );

      controlsActions.setLanguage(
        TrackType.audio,
        [defaultAudio.language, defaultAudio.kind].join(',')
      );
    }
  },
  loadSubtitle(kind: TrackKind, language: LanguageCode2Union) {
    const restoredTrackId = controlsStore.controlsApi?.setTextTrackId?.({
      language,
      kind,
    });

    if (restoredTrackId) {
      controlsActions.setLanguage(
        TrackType.text,
        [restoredTrackId.language, restoredTrackId.kind].join(',')
      );
      console.debug(`Restored previously used subtitle:`, restoredTrackId);
    }
  },
  loadLanguageTrack(type: TrackType) {
    const savedTrackId = localStorage.getItem(localStorageKeys[type]) ?? '';
    const [language, kind] = savedTrackId.split(',') as [
      LanguageCode2Union,
      TrackKind,
    ];

    if (type === TrackType.audio) {
      controlsActions.loadAudio(kind, language);
    }

    if (!language) {
      return;
    }

    if (type === TrackType.text) {
      controlsActions.loadSubtitle(kind, language);
    }
  },
  setLanguage(type: TrackType, key: string) {
    const [language, kind] = key.split(',') as [LanguageCode2Union, TrackKind];

    if (type === TrackType.audio && key !== controlsStore.selectedAudioTrack) {
      controlsStore.controlsApi?.setAudioTrackLang?.({
        language,
        kind,
      });
      localStorage.setItem(localStorageKeys[type], key);
      controlsStore.selectedAudioTrack = key || LanguageCode2.default;
    } else if (
      type === TrackType.text &&
      key !== controlsStore.selectedSubtitleTrack
    ) {
      controlsStore.controlsApi?.setTextTrackId?.({
        language,
        kind,
      });
      localStorage.setItem(localStorageKeys[type], key);
      controlsStore.selectedSubtitleTrack = key;
    }
  },
  updateAudioTracks() {
    const sortedTracks = sortLanguages(
      controlsStore.controlsApi?.getAudioTracksLang?.() ?? []
    );

    const tracks = sortedTracks.reduce<Record<string, string>>((acc, track) => {
      acc[[track.language, track.kind].join(',')] = prepareAudioName(
        track,
        playerStore.playerResources?.playerFeatures?.audioTracks?.namingBehavior
      );

      return acc;
    }, {});

    if (!Object.keys(tracks).length) {
      tracks[LanguageCode2.default] = Languages.Default;
    }

    controlsStore.audioTracks = tracks;
  },
  updateSubtitleTracks(reload?: boolean) {
    const sortedTracks = sortLanguages(
      controlsStore.controlsApi?.getTextTrackIds?.() ?? []
    );

    const tracks = sortedTracks.reduce<Record<string, string>>((acc, track) => {
      acc[[track.language, track.kind].join(',')] = prepareSubtitleName(
        track,
        playerStore.playerResources?.playerFeatures?.subtitleTracks
          ?.namingBehavior
      );

      return acc;
    }, {});

    if (Object.keys(tracks).length) {
      controlsStore.subtitleTracks = {
        off: languageCode2ToLanguage[LanguageCode2.off],
        ...tracks,
      };
    }

    if (reload) {
      const keyFromLS = localStorage.getItem(StorageKeys.SUBTITLE_TRACK);

      if (keyFromLS) {
        controlsStore.selectedSubtitleTrack =
          keyFromLS in controlsStore.subtitleTracks
            ? keyFromLS
            : LanguageCode2.off;
      }
    }
  },
  setIsSeeking(isSeeking: boolean) {
    controlsStore.isSeeking = isSeeking;
  },
  setVisibility(isVisible: boolean) {
    controlsStore.isVisible = isVisible;
  },
  setShowSpinner(showSpinner: boolean) {
    controlsStore.showSpinner = showSpinner;
  },
  setShowVideoTitleCenter(showVideoTitleCenter: boolean) {
    controlsStore.showVideoTitleCenter = showVideoTitleCenter;
  },
  setTouched(touched: number | null) {
    controlsStore.touched = touched;
  },
  setIsLanguageOverlayOpen(isLanguageOverlayOpen: boolean) {
    if (isLanguageOverlayOpen) {
      controlsActions.stopTimer('controls');
      controlsActions.stopTimer('cursor');
    } else {
      controlsActions.startTimer({ controls: true, cursor: true });
    }
    controlsStore.isLanguageOverlayOpen = isLanguageOverlayOpen;
  },
  setIsVolumeOverlayOpen(isVolumeOverlayOpen: boolean) {
    if (isVolumeOverlayOpen) {
      controlsActions.stopTimer('controls');
      controlsActions.stopTimer('cursor');
    } else {
      controlsActions.startTimer({ controls: true, cursor: true });
    }
    controlsStore.isVolumeOverlayOpen = isVolumeOverlayOpen;
  },
  setIsPlaybackSpeedOverlayOpen(isPlaybackSpeedOverlayOpen: boolean) {
    if (isPlaybackSpeedOverlayOpen) {
      controlsActions.stopTimer('controls');
      controlsActions.stopTimer('cursor');
    } else {
      controlsActions.startTimer({ controls: true, cursor: true });
    }
    controlsStore.isPlaybackSpeedOverlayOpen = isPlaybackSpeedOverlayOpen;
  },
  setPlaybackRate(rate: number) {
    const newRate =
      Number.isFinite(rate) && rate > 0
        ? limit(rate, MIN_PLAYBACK_SPEED, MAX_PLAYBACK_SPEED)
        : NORMAL_SPEED;

    controlsStore.playbackRate = newRate;

    if (playerStore.player) {
      playerStore.player.playbackRate = newRate;
    }
  },
  // Stores volume which should be a number between 0 and 1
  setVolume(volume: number) {
    if (controlsStore.volume === volume) {
      return;
    }
    const newVolume = limit(volume, 0, 1);
    controlsStore.volume = newVolume;

    if (!playerStore.player) {
      return;
    }

    playerStore.player.muted = !newVolume;

    if (newVolume > 0) {
      playerStore.player.volume = newVolume;
    }
  },
  setFullScreen(isFullScreen: boolean) {
    controlsStore.isFullScreen = isFullScreen;
  },
  showCursor() {
    // Use the cursor specified in the CSS file.
    if (playerStore.container) {
      playerStore.container.style.cursor = 'initial';
    }
  },
  updatePlayerContentState() {
    const isPaused = playerStore.player?.paused && !controlsStore.isSeeking;
    controlsStore.contentState = isPaused
      ? PlayerContentState.Play
      : PlayerContentState.Pause;
  },
  startTimer(options: StartTimerOptions) {
    const { controls, cursor, immediately } = options;

    // If a modal is opened, don't do anything
    if (
      controlsStore.isLanguageOverlayOpen ||
      controlsStore.isVolumeOverlayOpen ||
      controlsStore.isPlaybackSpeedOverlayOpen
    ) {
      return;
    }

    // if the player is still hidden behind the loading spinner, don't count down yet
    if (!controlsStore.showPlayer) {
      return;
    }

    if (controls) {
      if (immediately) {
        fadeControlsTimer.tickNow();
      } else {
        fadeControlsTimer.tickAfter(controlsConfig.fadeOutDelay);
      }
    }

    if (cursor) {
      if (immediately) {
        mouseStillTimer.tickNow();
      } else {
        mouseStillTimer.tickAfter(controlsConfig.fadeOutDelay);
      }
    }
  },
  stopTimer(timer: 'controls' | 'cursor') {
    switch (timer) {
      case 'controls':
        fadeControlsTimer.stop();
        break;
      case 'cursor':
        mouseStillTimer.stop();
        break;
      default:
        console.warn(`Timer ${timer} does not exist`);
    }
  },
  computeOpacity(options?: ComputeOpacityOptions) {
    const { forceHide, ignoreTouched } = options || {};

    // If a modal is opened, don't do anything
    if (
      controlsStore.isLanguageOverlayOpen ||
      controlsStore.isVolumeOverlayOpen ||
      controlsStore.isPlaybackSpeedOverlayOpen
    ) {
      return;
    }

    // if the player is still hidden behind the loading spinner, don't count down yet
    // `setShowPlayer` calls us again once the player is actually on screen
    if (!controlsStore.showPlayer) {
      return;
    }

    // We sometimes get 'mouseout' events with touches.  Since we can never
    // leave the video element when touching, ignore.
    if (ignoreTouched && controlsStore.touched) {
      return;
    }

    const isPaused = playerStore.player?.paused && !controlsStore.isSeeking;

    // Keep showing the controls if the ad or video is paused or there has been
    // recent mouse movement
    if (isPaused || forceHide) {
      // If mouse is leaving the area when the content is not paused we hide the controls immediately
      controlsActions.setVisibility(!!isPaused || !forceHide);
      fadeControlsTimer.stop();
    } else {
      fadeControlsTimer.tickAfter(controlsConfig.fadeOutDelay);
    }
  },
  handlePlayPause() {
    const {
      playbackControl: { doesControlPlayback, isAutoplayAllowed, hasPlayed },
      isInsideAd,
      isAdPlaying,
    } = adsStore;

    // Calculate isPaused based on player state or ad state
    const isPaused =
      (playerStore.player?.paused && !controlsStore.isSeeking) ||
      (doesControlPlayback && isInsideAd && !isAdPlaying);

    if (!playerStore.player) {
      // Media is not loaded yet, ignore click event
      return;
    }

    if (isPaused) {
      if (doesControlPlayback && !isAutoplayAllowed && !hasPlayed) {
        adsActions.startAds();

        adsActions.setPlaybackControl({
          hasPlayed: true,
        });

        return;
      }

      if (doesControlPlayback && handleAdPlayback(false)) {
        return;
      }

      playerStore.player.play().catch(ignoreError);
    } else {
      playerStore.player.pause();
      doesControlPlayback && handleAdPlayback(false);
    }
    controlsActions.updatePlayerContentState();
  },
  setShowPlayer(showPlayer: boolean) {
    const hasBecomeVisible = showPlayer && !controlsStore.showPlayer;

    controlsStore.showPlayer = showPlayer;

    // the fade out countdown is skipped while the player is hidden so it has to be started here, when the player finally shows up
    if (hasBecomeVisible) {
      controlsActions.computeOpacity();
    }
  },
  reset() {
    resetStore(controlsStore, initialControlsStore, ['isFullScreen']);
  },
};

export const useControlsStore = () =>
  useSnapshot(controlsStore) as ControlsStore;

devtoolsApi.setStore('controls', controlsStore, controlsActions);
